import moment from 'moment-timezone';
import type {
  AvailableScheduledTask,
  ScheduledTaskTemplate,
  Calendar,
} from './types';

export const getAvailableScheduledTasks = (
  currentUserEmail?: string
): AvailableScheduledTask[] => [
  {
    workflowType: 'orgDeviceFrontendUpdate',
    label: 'Device Frontend Update',
    optionDefinitions: [
      {
        name: 'batchSize',
        type: 'integer',
        label: 'Devices to process at once',
        required: true,
        default: 10,
      },
    ],
  },
  {
    workflowType: 'orgMLTraining',
    label: 'ML Training',
    optionDefinitions: [
      {
        name: 'trainingPeriod',
        type: 'period',
        label: 'Training Period',
        required: true,
        default: 'ONE_MONTH',
      },
    ],
  },
  {
    workflowType: 'generateReports',
    label: 'Scheduled Reports',
    optionDefinitions: [
      {
        name: 'emailSubject',
        type: 'string',
        label: 'Email Subject',
        required: true,
        default: 'Scheduled Reports',
      },
      {
        name: 'emailRecipients',
        type: 'email',
        label: 'Email Recipients',
        required: true,
        default: currentUserEmail ? [currentUserEmail] : [],
      },
      {
        name: 'attachPDFs',
        type: 'boolean',
        label: 'Attach Report PDFs to Email',
        required: false,
        default: true,
      },
      {
        name: 'attachCSVs',
        type: 'boolean',
        label: 'Attach Report CSVs to Email',
        required: false,
        default: true,
      },
    ],
  },
  {
    workflowType: 'autoSuspendResumeActivity',
    label: 'Suspend/Resume Activity',
    optionDefinitions: [
      {
        name: 'suspendMins',
        type: 'integer',
        label: 'Minutes to Suspend Active Labor Tickets',
        required: true,
        default: 30,
      },
      {
        name: 'clockOut',
        type: 'boolean',
        label: 'Also Clock Out/In Employee on Suspend/Resume',
        required: false,
        default: false,
      },
      {
        name: 'shiftFilterType',
        type: 'select',
        label: 'Applicable Shifts',
        required: true,
        default: 'all',
        values: ['all', 'exclude', 'include'],
        valueLabels: ['All', 'Exclude', 'Include'],
      },
      {
        name: 'shiftNums',
        type: 'select',
        multiple: true,
        label: 'Shifts',
        required: false,
        default: [],
        configuration: {
          itemType: 'shifts',
          listViewColumns: [{ field: 'shiftNum' }],
        },
        labelColumns: ['shiftNum'],
        idColumn: 'shiftNum',
        dependencies: [
          {
            field: 'shiftFilterType',
            condition: 'not_equals',
            value: 'all',
            action: 'show',
          },
        ],
      },
      {
        name: 'employeeFilterType',
        type: 'select',
        label: 'Applicable Employees',
        required: true,
        default: 'all',
        values: ['all', 'exclude', 'include'],
        valueLabels: ['All', 'Exclude', 'Include'],
      },
      {
        name: 'employeeIds',
        type: 'select',
        multiple: true,
        label: 'Employees',
        required: false,
        default: [],
        configuration: {
          itemType: 'employees',
          listViewColumns: [
            { field: 'firstName' },
            { field: 'lastName' },
            { field: 'empID' },
          ],
        },
        labelColumns: ['firstName', 'lastName'],
        idColumn: 'empID',
        dependencies: [
          {
            field: 'employeeFilterType',
            condition: 'not_equals',
            value: 'all',
            action: 'show',
          },
        ],
      },
      {
        name: 'resourceFilterType',
        type: 'select',
        label: 'Applicable Resources',
        required: true,
        default: 'all',
        values: ['all', 'exclude', 'include'],
        valueLabels: ['All', 'Exclude', 'Include'],
      },
      {
        name: 'resourceIds',
        type: 'select',
        multiple: true,
        label: 'Resources',
        required: false,
        default: [],
        configuration: {
          itemType: 'resources',
          listViewColumns: [{ field: 'resourceID' }, { field: 'description' }],
        },
        labelColumns: ['resourceID', 'description'],
        idColumn: 'resourceID',
        dependencies: [
          {
            field: 'resourceFilterType',
            condition: 'not_equals',
            value: 'all',
            action: 'show',
          },
        ],
      },
    ],
  },
  {
    workflowType: 'autoClockOut',
    label: 'Auto Clock-Out',
    optionDefinitions: [
      {
        name: 'thresholdHours',
        type: 'integer',
        label: 'Threshold Hours (clock out after this many hours)',
        required: true,
        default: 16,
      },
      {
        name: 'emailRecipients',
        type: 'email',
        label: 'Email Recipients',
        required: true,
        default: currentUserEmail ? [currentUserEmail] : [],
      },
      {
        name: 'emailSubject',
        type: 'string',
        label: 'Email Subject',
        required: true,
        default: 'Auto Clock-Out Summary',
      },
      {
        name: 'dryRun',
        type: 'boolean',
        label: 'Dry Run (send email only, do not clock out)',
        required: false,
        default: false,
      },
      {
        name: 'shiftFilterType',
        type: 'select',
        label: 'Applicable Shifts',
        required: true,
        default: 'all',
        values: ['all', 'exclude', 'include'],
        valueLabels: ['All', 'Exclude', 'Include'],
      },
      {
        name: 'shiftNums',
        type: 'select',
        multiple: true,
        label: 'Shifts',
        required: false,
        default: [],
        configuration: {
          itemType: 'shifts',
          listViewColumns: [{ field: 'shiftNum' }],
        },
        labelColumns: ['shiftNum'],
        idColumn: 'shiftNum',
        dependencies: [
          {
            field: 'shiftFilterType',
            condition: 'not_equals',
            value: 'all',
            action: 'show',
          },
        ],
      },
      {
        name: 'departmentFilterType',
        type: 'select',
        label: 'Applicable Departments',
        required: true,
        default: 'all',
        values: ['all', 'exclude', 'include'],
        valueLabels: ['All', 'Exclude', 'Include'],
      },
      {
        name: 'departmentIds',
        type: 'select',
        multiple: true,
        label: 'Departments',
        required: false,
        default: [],
        configuration: {
          itemType: 'departments',
          listViewColumns: [{ field: 'departmentID' }, { field: 'description' }],
        },
        labelColumns: ['departmentID', 'description'],
        idColumn: 'departmentID',
        dependencies: [
          {
            field: 'departmentFilterType',
            condition: 'not_equals',
            value: 'all',
            action: 'show',
          },
        ],
      },
      {
        name: 'exemptEmployeeIds',
        type: 'select',
        multiple: true,
        label: 'Exempt Employees',
        required: false,
        default: [],
        configuration: {
          itemType: 'employees',
          listViewColumns: [
            { field: 'firstName' },
            { field: 'lastName' },
            { field: 'empID' },
          ],
        },
        labelColumns: ['firstName', 'lastName', 'empID'],
        idColumn: 'empID',
      },
    ],
  },
  {
    workflowType: 'warmStaticCache',
    label: 'Warm Static Cache',
    optionDefinitions: [
      {
        name: 'items',
        type: 'select',
        multiple: true,
        label: 'Item Types to Warm',
        required: true,
        default: [
          'employees',
          'parts',
          'reasons',
          'departments',
          'revisions',
          'assemblies',
          'shifts',
          'resources',
          'resourceGroups',
        ],
        values: [
          'employees',
          'parts',
          'reasons',
          'departments',
          'revisions',
          'assemblies',
          'shifts',
          'resources',
          'resourceGroups',
        ],
        valueLabels: [
          'Employees',
          'Parts',
          'Reasons',
          'Departments',
          'Revisions',
          'Assemblies',
          'Shifts',
          'Resources',
          'Resource Groups',
        ],
      },
    ],
  },
  {
    workflowType: 'buildHistTables',
    label: 'Build Historical Tables',
    optionDefinitions: [
      {
        name: 'histTypes',
        type: 'select',
        multiple: true,
        label: 'Historical Table Types',
        required: true,
        default: ['teepByResourceDay'],
        values: ['teepByResourceDay'],
        valueLabels: ['TEEP by Resource by Day'],
      },
      {
        name: 'lookbackDays',
        type: 'integer',
        label: 'Days to Look Back',
        required: true,
        default: 7,
      },
      {
        name: 'resourceFilterType',
        type: 'select',
        label: 'Resource Selection',
        required: true,
        default: 'harmoniDevices',
        values: ['harmoniDevices', 'all', 'resources', 'deviceGroups'],
        valueLabels: [
          'Resources with Devices',
          'All Resources',
          'Specific Resources',
          'Device Groups',
        ],
      },
      {
        name: 'resourceIds',
        type: 'select',
        multiple: true,
        label: 'Resources',
        required: false,
        default: [],
        configuration: {
          itemType: 'resources',
          listViewColumns: [{ field: 'resourceID' }, { field: 'description' }],
        },
        labelColumns: ['resourceID', 'description'],
        idColumn: 'resourceID',
        dependencies: [
          {
            field: 'resourceFilterType',
            condition: 'equals',
            value: 'resources',
            action: 'show',
          },
        ],
      },
      {
        name: 'deviceGroupIds',
        type: 'deviceGroup',
        multiple: true,
        label: 'Device Groups',
        required: false,
        default: [],
        dependencies: [
          {
            field: 'resourceFilterType',
            condition: 'equals',
            value: 'deviceGroups',
            action: 'show',
          },
        ],
      },
    ],
  },
  {
    workflowType: 'syncDeviceOfflineMachineRecs',
    label: 'Mark Offline Devices in Machine Records',
    optionDefinitions: [
      {
        name: 'offlineThresholdMins',
        type: 'integer',
        label: 'Minutes Offline Before Marking Machine Offline',
        required: true,
        default: 15,
      },
    ],
  },
];

const WARM_STATIC_CACHE_LOCAL_HOUR = 3;

const ALL_WEEK_DAYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

const buildDailyLocalHourCalendars = (
  localHour: number,
  timeZone?: string
): Calendar[] => {
  const tz = timeZone || 'America/New_York';
  const utc = moment
    .tz(
      `${moment().format('YYYY-MM-DD')} ${String(localHour).padStart(2, '0')}:00`,
      tz
    )
    .utc();
  const utcHour = utc.hour();
  const utcMinute = utc.minute();
  return ALL_WEEK_DAYS.map((day) => ({
    dayOfWeek: [{ start: day, end: day }],
    hour: [{ start: utcHour, end: utcHour }],
    minute: [{ start: utcMinute, end: utcMinute }],
  }));
};

export const systemScheduledTasks: ScheduledTaskTemplate[] = [
  {
    workflowType: 'orgDeviceFrontendUpdate',
    scheduleName: 'HarmoniFrontendUpdate',
    options: [
      10,
    ],
    calendars: [
      'SUNDAY',
      'MONDAY',
      'TUESDAY',
      'WEDNESDAY',
      'THURSDAY',
      'FRIDAY',
      'SATURDAY',
    ].map((day) => ({
      dayOfWeek: [{ start: day, end: day }],
      hour: [{ start: 2, end: 2 }],
      minute: [{ start: 0, end: 0 }],
    })),
  },
  {
    workflowType: 'orgMLTraining',
    scheduleName: 'HarmoniMLTraining',
    options: [
      'ONE_MONTH',
    ],
    calendars: [
      {
        dayOfWeek: [{ start: 'FRIDAY', end: 'FRIDAY' }],
        hour: [{ start: 3, end: 3 }],
        minute: [{ start: 0, end: 0 }],
      },
    ],
  },
  {
    workflowType: 'buildHistTables',
    scheduleName: 'HarmoniBuildHistTables',
    options: [
      ['teepByResourceDay'],
      7,
      'harmoniDevices',
      [],
      [],
    ],
    calendars: [
      'SUNDAY',
      'MONDAY',
      'TUESDAY',
      'WEDNESDAY',
      'THURSDAY',
      'FRIDAY',
      'SATURDAY',
    ].map((day) => ({
      dayOfWeek: [{ start: day, end: day }],
      hour: [{ start: 4, end: 4 }],
      minute: [{ start: 0, end: 0 }],
    })),
  },
  {
    workflowType: 'warmStaticCache',
    scheduleName: 'HarmoniWarmStaticCache',
    options: [
      [
        'employees',
        'parts',
        'reasons',
        'departments',
        'revisions',
        'assemblies',
        'shifts',
        'resources',
        'resourceGroups',
      ],
    ],
    calendars: buildDailyLocalHourCalendars(WARM_STATIC_CACHE_LOCAL_HOUR),
  },
  {
    workflowType: 'syncDeviceOfflineMachineRecs',
    scheduleName: 'HarmoniDeviceOfflineMachineRecs',
    options: [15],
    calendars: [],
    intervals: [{ every: '15m' }],
  },
];

export const getSystemScheduledTasks = (
  timeZone?: string
): ScheduledTaskTemplate[] =>
  systemScheduledTasks.map((task) =>
    task.workflowType === 'warmStaticCache'
      ? {
          ...task,
          calendars: buildDailyLocalHourCalendars(
            WARM_STATIC_CACHE_LOCAL_HOUR,
            timeZone
          ),
        }
      : task
  );
